/* eslint-disable react/jsx-props-no-spreading */
import type * as React from 'react';
import classNames from 'classnames';
import { Button } from '@doda/io-react/common';
import type { JobResponse } from '@doda/api-client/aspida/@types';
import { APPLY_BUTTON_TEXT_VARIANTS } from '@doda/common/src/constants/jobSearchDetail';
import { JOB_OFFER_KIND_TYPE_AGENT } from '../../constants';
import styles from './index.module.scss';

type IOButtonProps = React.ComponentProps<typeof Button>;

/**
 * CommonApplyButtonProps
 */
export interface CommonApplyButtonProps
  extends Pick<IOButtonProps, 'widthPercentage' | 'size' | 'onClick'> {
  /** デザインシステム定義サイズを指定 */
  size: 'S' | 'M' | 'L';
  /** クリック時に実行する処理  */
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  /** ボタンのテキスト */
  text?: string;
}

const DirectApplyButton = (props: CommonApplyButtonProps) => {
  const { text = '応募する', ...rest } = props;
  return (
    <Button variant="blue" {...rest}>
      {text}
    </Button>
  );
};

const AgentApplyButton = (props: CommonApplyButtonProps) => {
  const { size, text = '応募依頼する', ...rest } = props;
  return (
    <Button variant="orange" size={size} {...rest}>
      <span>
        {text}
        <span
          className={classNames(
            styles['jobSearchDetail-applyButton__subText'],
            {
              [styles['jobSearchDetail-applyButton__subText--S']]: size === 'S',
              [styles['jobSearchDetail-applyButton__subText--M']]: size === 'M',
              [styles['jobSearchDetail-applyButton__subText--L']]: size === 'L',
            }
          )}
        >
          （エージェントサービス）
        </span>
      </span>
    </Button>
  );
};

/**
 * ApplyButtonProps
 */
export interface ApplyButtonProps
  extends Omit<CommonApplyButtonProps, 'widthPercentage'> {
  /** 求人種別 */
  jobOfferKind: JobResponse['jobOfferKind'];
  /** 任意の幅を指定 */
  width?: React.CSSProperties['width'];
  /** ABテストグループ番号 */
  abTestGroupNo?: string;
}

/** 応募ボタンUI */
export const ApplyButton = (props: ApplyButtonProps) => {
  const { jobOfferKind, width, size, onClick, abTestGroupNo } = props;
  const isAgent = jobOfferKind === JOB_OFFER_KIND_TYPE_AGENT;

  const variantKey = (abTestGroupNo ||
    '0') as keyof typeof APPLY_BUTTON_TEXT_VARIANTS;
  const variant =
    variantKey in APPLY_BUTTON_TEXT_VARIANTS
      ? APPLY_BUTTON_TEXT_VARIANTS[variantKey]
      : null;
  const selectedText = isAgent ? variant?.agentApply : variant?.directApply;
  const buttonText = variant ? selectedText : undefined;

  const buttonProps: CommonApplyButtonProps = {
    size,
    widthPercentage: width ? 100 : undefined,
    onClick,
  };

  return (
    <div style={{ width }}>
      {isAgent ? (
        <AgentApplyButton {...buttonProps} text={buttonText} />
      ) : (
        <DirectApplyButton {...buttonProps} text={buttonText} />
      )}
    </div>
  );
};
